import {
  AdminSpinner,
  ArrowRed,
  BestPerformingBrandsEntity,
  Box,
  Div,
  getDashboardBestPerformingBrands,
  H5,
  Icon,
  lightTheme,
  ModalPortal,
  BestPerformingBrandComponent,
  BestPerformingPlaceholder,
  reportError,
} from '@vgrubs/components';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

async function getBestPerformingBrands(
  page: number,
  bestPerformingBrands: BestPerformingBrandsEntity[] | null,
  setBestPerformingBrands: Dispatch<
    SetStateAction<BestPerformingBrandsEntity[] | null>
  >,
  hasMore: boolean,
  setHasMore: Dispatch<SetStateAction<boolean>>,
  setPage: Dispatch<SetStateAction<number>>,
  setLoading: Dispatch<SetStateAction<boolean>>
) {
  if (!hasMore) return;
  setLoading(true);

  const LIMIT = 10;

  try {
    const { data } = await getDashboardBestPerformingBrands({
      page,
      limit: LIMIT,
    });

    if (bestPerformingBrands === null) {
      setBestPerformingBrands(data.data);
    } else {
      setBestPerformingBrands((prev) => [...(prev || []), ...data.data]);
    }

    setHasMore(data.data.length === LIMIT);
    setPage(page + 1);
  } catch (error) {
    reportError(error);
  } finally {
    setLoading(false);
  }
}

export const BestPerformingBrandsSection = () => {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [bestPerformingBrands, setBestPerformingBrands] = useState<
    BestPerformingBrandsEntity[] | null
  >(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [ref, inView] = useInView();

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    getBestPerformingBrands(
      1,
      null,
      setBestPerformingBrands,
      true,
      setHasMore,
      setPage,
      setLoading
    );
  }, []);

  useEffect(() => {
    if (inView && hasMore) {
      getBestPerformingBrands(
        page,
        bestPerformingBrands,
        setBestPerformingBrands,
        hasMore,
        setHasMore,
        setPage,
        setLoadingMore
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  return (
    <>
      <Box $p="16px 20px" $relative $overflow="hidden">
        {loading && <AdminSpinner small absolute creme />}
        {bestPerformingBrands?.length ? (
          <Icon
            $pointer
            style={{ position: 'absolute', right: 16, top: 16 }}
            onClick={() => setIsOpen(true)}
            color={lightTheme.lightGray}
          >
            <ArrowRed />
          </Icon>
        ) : null}
        <H5 $mb="16">Best Performing Brands</H5>

        {bestPerformingBrands?.slice(0, 5).map((item, index) => (
          <BestPerformingBrandComponent key={index} item={item} index={index} />
        ))}

        <BestPerformingPlaceholder length={bestPerformingBrands?.length || 0} />
      </Box>
      <ModalPortal
        isOpen={isOpen}
        closeModal={() => setIsOpen(false)}
        backgroundColor={lightTheme.white}
        padding="24px"
        logo={false}
        width="600px"
        maxHeight="calc(65px * 10)"
        modalOverflow="hidden"
        titleWithExit="Best Performing Brands"
      >
        {loadingMore && <AdminSpinner small absolute />}

        {bestPerformingBrands?.map((item, index) => (
          <BestPerformingBrandComponent key={index} item={item} index={index} />
        ))}
        <Div $height="20px" ref={ref}></Div>
      </ModalPortal>
    </>
  );
};
